"use client";

import { RiUserAddLine } from "@remixicon/react";
import type { UseSearchHook, UseSearchStateReturn } from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import type { HTMLAttributes, ReactNode } from "react";
import { useEffect, useRef } from "react";
import type { TopNavItem } from "./top-nav-item";
import { NavItem } from "./top-nav-item";
import { TopNavLogo } from "./top-nav-logo";
import { TopNavNotifications } from "./top-nav-notifications";
import { TopNavProfile, type TopNavUser } from "./top-nav-profile";
import { TopNavProfilePublic } from "./top-nav-profile-public";
import { TopNavSearch } from "./top-nav-search";

// Main TopNav props
export interface TopNavProps extends HTMLAttributes<HTMLElement> {
	// Authentication state
	user?: TopNavUser | null;
	isLoading?: boolean;

	// Navigation items
	items?: TopNavItem[];

	// Event handlers
	onSignOut?: () => void;
	onNavigateToFestival?: (festivalSlug: string) => void;
	onNavigateToNavItem?: (url: string) => void;
	onReportIssues?: () => void;

	// Enhanced hooks (optional) - legacy props for future integrations
	// Using unknown for type safety since these are not currently used
	useNotificationsHook?: unknown;
	useMarkNotificationsReadHook?: unknown;
	useDismissNotificationsHook?: unknown;
	useNotificationState?: unknown;
	useSearchState?: () => UseSearchStateReturn;
	useSearchHook?: UseSearchHook;
	useProfileState?: unknown;

	children?: ReactNode;
}

export function TopNav({
	user,
	isLoading = false,
	items = [],
	onSignOut,
	onNavigateToFestival,
	onNavigateToNavItem,
	onReportIssues,
	useSearchState,
	useSearchHook,
	className,
	...props
}: TopNavProps) {
	// Track if we've completed initial mount
	const hasInitiallyLoaded = useRef(false);

	useEffect(() => {
		if (!isLoading) {
			hasInitiallyLoaded.current = true;
		}
	}, [isLoading]);

	// Only show loading state if we haven't loaded initially yet
	const showLoadingState = isLoading && !hasInitiallyLoaded.current;
	const handleNavigateToFestivalWrapper = (festivalSlug: string) => {
		if (user && onNavigateToFestival) {
			onNavigateToFestival(festivalSlug);
		} else {
			window.location.href = `/festivals/${festivalSlug}`;
		}
	};

	// Determine if we should show authenticated or public nav
	const isAuthenticated = !!user;
	const isAnonymous = !user;

	return (
		<nav
			className={cn(
				"fixed inset-x-0 top-0 z-50 w-full border-b pt-safe",
				"bg-background",
				className,
			)}
			style={{
				height: "calc(3.5rem + env(safe-area-inset-top))",
			}}
			{...props}
		>
			<div className="flex h-14 items-center px-4">
				{/* Left side - Logo + Nav items (mobile) */}
				<div className="flex items-center gap-2">
					<TopNavLogo />
					{/* Mobile navigation items */}
					<div className="flex space-x-1 md:hidden">
						{items.map((item) => (
							<NavItem key={item.title} item={item} variant="mobile" />
						))}
						{/* Show sign up for anonymous users */}
						{isAnonymous && (
							<button
								type="button"
								onClick={() => onNavigateToNavItem?.("/auth/signup")}
								className={cn(
									"group flex h-9 items-center gap-2 rounded-xl transition-all duration-200",
									"px-3",
									"text-muted-foreground hover:bg-muted/40 hover:text-foreground active:scale-95 active:bg-muted/60",
								)}
							>
								<RiUserAddLine
									className={cn(
										"h-4 w-4",
										"text-muted-foreground group-hover:text-foreground",
									)}
								/>
							</button>
						)}
					</div>
				</div>

				{/* Center area - Nav items (desktop) for all users, plus sign up for anonymous */}
				<div className="relative flex flex-1 items-center justify-center">
					{/* Desktop navigation items */}
					<div className="hidden space-x-1 md:flex md:flex-1 md:items-center md:justify-center md:pl-16">
						{items.map((item) => (
							<NavItem key={item.title} item={item} variant="desktop" />
						))}

						{/* Show sign up for anonymous users on desktop */}
						{isAnonymous && (
							<button
								type="button"
								onClick={() => onNavigateToNavItem?.("/auth/signup")}
								className={cn(
									"group flex h-9 items-center gap-2 rounded-xl transition-all duration-200",
									"px-4",
									"text-muted-foreground hover:bg-muted/40 hover:text-foreground active:scale-95 active:bg-muted/60",
								)}
							>
								<RiUserAddLine
									className={cn(
										"h-4 w-4",
										"text-muted-foreground group-hover:text-foreground",
									)}
								/>
								<span className="font-medium text-sm">Sign Up</span>
							</button>
						)}
					</div>
				</div>

				{/* Right side */}
				<div className="flex justify-end gap-3">
					<TopNavSearch
						onNavigateToFestival={handleNavigateToFestivalWrapper}
						useSearchState={useSearchState}
						useSearchHook={useSearchHook}
					/>

					{/* Auth-dependent UI */}
					{showLoadingState ? (
						// Show loading state only during initial load
						<div className="flex gap-3">
							<div className="h-8 w-8 animate-pulse rounded-full bg-muted/50" />
							<div className="h-8 w-8 animate-pulse rounded-full bg-muted/50" />
						</div>
					) : isAuthenticated ? (
						// Authenticated: Show notifications and profile
						<>
							<TopNavNotifications />
							<TopNavProfile
								user={user!}
								onSignOut={onSignOut}
								onReportIssues={onReportIssues}
							/>
						</>
					) : (
						// Public: Show settings menu
						<TopNavProfilePublic onReportIssues={onReportIssues} />
					)}
				</div>
			</div>
		</nav>
	);
}
